import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import jwtAxios, { setAuthToken } from './index';
import { useNavigate } from 'react-router-dom';
import { useInfoViewActionsContext } from '../../../../context/src/AppContextProvider/InfoViewContextProvider';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { PushNotifications } from '@capacitor/push-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { handleFcmToken } from './fcmHelper';
import { Capacitor } from '@capacitor/core';
import { isStudent } from '@projectK/context/RolesProvider';
import { useDashboard } from '@projectK/context/DashboardProvider';
import {
  applyTournamentRegistrationHideNavIfStartedLoggedOut,
  clearStaleTournamentRegistrationState,
  clearTournamentRegistrationSession,
  completeTournamentLoginRedirect,
  getTournamentWidgetReturnPath,
  shouldResumeTournamentRegistrationAfterLogin,
} from '@projectK/modules/ecommerce/TournamentDetail';

const JWTAuthContext = createContext();
const JWTAuthActionsContext = createContext();

const LOCKED_ACCOUNT_ERROR = 'Contact an admin for further assistance';

function resolvePostLoginPath({
  isStudentOrParticipant,
  roleFlag,
  parts,
  slug,
  customer_id,
}) {
  const tournamentWidgetPath = getTournamentWidgetReturnPath();
  if (
    tournamentWidgetPath &&
    shouldResumeTournamentRegistrationAfterLogin() &&
    roleFlag !== 'parent' &&
    roleFlag !== 'student' &&
    roleFlag !== 'coach'
  ) {
    return `/${tournamentWidgetPath}`;
  }
  clearStaleTournamentRegistrationState();

  const prevSegment = parts[parts.length - 2];
  const lastSegment = parts[parts.length - 1];
  const pendingInvoiceSearch =
    typeof window !== 'undefined' &&
    localStorage.getItem('searchByInvoiceOrder');
  const pendingUnsubscribe =
    typeof window !== 'undefined' && localStorage.getItem('unsubscribe');

  if (pendingInvoiceSearch) {
    return '/transaction-history';
  }

  if (pendingUnsubscribe) {
    return '/user/unsubscribe';
  }

  if (isStudentOrParticipant && prevSegment === 'products') {
    return `/${roleFlag}/waiver/?refurl=/products/${slug}`;
  }
  if (isStudentOrParticipant && prevSegment === 'packages') {
    return `/${roleFlag}/waiver/?refurl=/packages/${slug}`;
  }
  if (isStudentOrParticipant) {
    return `/${roleFlag}/waiver`;
  }
  if (prevSegment === 'order_invoice') {
    return '/transaction-history';
  }
  if (lastSegment === 'unsubscribe-email') {
    return '/user/unsubscribe';
  }
  if (customer_id) {
    return `/${roleFlag}/events`;
  }
  return null;
}

const isEmailDeepLinkPath = () => {
  const pathname = window.location.pathname;
  return (
    pathname.includes('/order_invoice/') ||
    pathname.includes('/user/unsubscribe-email')
  );
};

export const useJWTAuth = () => useContext(JWTAuthContext);

export const useJWTAuthActions = () => useContext(JWTAuthActionsContext);

const JWTAuthAuthProvider = ({
  children,
  fetchStart,
  fetchSuccess,
  fetchError,
}) => {
  const { setShowDashboard } = useDashboard();
  const fullUrl = window.location.href;
  const parts = fullUrl.split(/%2F|\//);
  const slug = parts[parts.length - 1];
  const [jWTAuthData, setJWTAuthData] = useState({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

  const infoViewActionsContext = useInfoViewActionsContext();
  const navigate = useNavigate();

  useEffect(() => {
    clearStaleTournamentRegistrationState();
    fetchcompanyData();
    const getAuthUser = () => {
      fetchStart();

      jwtAxios
        .get('/auth')
        .then(({ data }) => {
          clearStaleTournamentRegistrationState();
          fetchSuccess();
          setJWTAuthData({
            user: data,
            isLoading: false,
            isAuthenticated: true,
          });
        })
        .catch(() => {
          setJWTAuthData({
            user: undefined,
            isLoading: false,
            isAuthenticated: false,
          });
          fetchSuccess();
        });
    };

    getAuthUser();
  }, []);

  useEffect(() => {
    const { user, isAuthenticated, isLoading } = jWTAuthData;
    if (!isAuthenticated || isLoading || !user || !isEmailDeepLinkPath()) {
      return;
    }

    const path = resolvePostLoginPath({
      isStudentOrParticipant: isStudent(user),
      roleFlag: user?.roleMenuFlag?.[0],
      parts,
      slug,
      customer_id: null,
      user,
    });

    if (path) {
      navigate(path, { replace: true });
    }
  }, [jWTAuthData.isAuthenticated, jWTAuthData.isLoading, jWTAuthData.user]);

  const [isListenerAttached, setIsListenerAttached] = useState(false);
  const storeToken = async (userId, token) => {
    localStorage.setItem('fcm_token', token);
    try {
      await jwtAxios.post('/fcm-token/store-token', {
        user_id: userId,
        fcm_token: token,
      });
    } catch (error) {
      console.error('Error storing FCM token:', error);
    }
  };

  const getFcmToken = async (userId) => {
    try {
      const platform = Capacitor.getPlatform();
      if (platform === 'android' || platform === 'ios') {
        const permission = await PushNotifications?.requestPermissions();
        if (permission.receive !== 'granted') {
          console.error('Push Notification permission denied');
          return;
        }

        await PushNotifications?.register();
      }
      if (platform === 'android') {
        PushNotifications?.addListener('registration', async (token) => {
          if (token?.value) {
            await storeToken(userId, token.value);
          } else {
            console.error('No token received on Android');
          }
        });

        PushNotifications?.addListener('registrationError', (error) => {
          console.error(
            'Push Notification Registration Error (Android):',
            error,
          );
        });
      } else if (platform === 'ios') {
        const { token } = await FirebaseMessaging.getToken();
        if (!token) {
          console.error('Failed to retrieve FCM token on iOS');
          return;
        }

        await storeToken(userId, token);
      }
    } catch (error) {
      console.error('Error with push notifications:', error);
    }
  };
  const fetchcompanyData = async (setVerbiageSettings) => {
    try {
      const response = await jwtAxios.get(`/public/app-settings/`);
      const { data } = response;
      if (data) {
        localStorage.setItem('theme', data?.basic?.theme);
        localStorage.setItem('verbiageSettings', JSON.stringify(data?.display));
        if (typeof setVerbiageSettings === 'function') {
          setVerbiageSettings(data?.display || {});
        }
      }
    } catch (error) {
      console.error('Error fetching appearance settings:', error);
      if (error?.response?.data?.error === 'No Subdomain found') {
        window.location.href = `${process.env.NX_BASE_DOMAIN}/404`;
      }
    }
  };

  const resetUnauthenticatedState = () => {
    setJWTAuthData(prev => ({
      ...prev,
      isAuthenticated: false,
      isLoading: false,
    }));
  };

  const handleSignInError = error => {
    console.error(error);
    resetUnauthenticatedState();
    const isLockedAccount =
      error?.response?.data?.error === LOCKED_ACCOUNT_ERROR;
    if (isLockedAccount) {
      setIsErrorDialogOpen(true);
      fetchError();
      return;
    }
    fetchError(error?.response?.data?.error || 'Something went wrong');
  };

  const establishAuthenticatedSession = async setVerbiageSettings => {
    const { data } = await jwtAxios.get('/auth');
    await handleFcmToken(
      data?.userId,
      getFcmToken,
      isListenerAttached,
      setIsListenerAttached,
    );
    setJWTAuthData({
      user: data,
      isAuthenticated: true,
      isLoading: false,
    });
    fetchcompanyData(setVerbiageSettings);
    return { data, isStudentOrParticipant: isStudent(data) };
  };

  const signInUser = async ({
    email,
    password,
    rememberMe,
    customer_id,
    setVerbiageSettings,
    isTournament,
  }) => {
    fetchStart();
    try {
      const loginRes = await jwtAxios.post('/auth/login', {
        email,
        password,
        rememberMe,
        customer_id,
      });
      if (loginRes.status !== 200) {
        return;
      }

      const { data, isStudentOrParticipant } =
        await establishAuthenticatedSession(setVerbiageSettings);

      applyTournamentRegistrationHideNavIfStartedLoggedOut();

      if (isTournament) {
        fetchSuccess();
        return;
      }

      fetchSuccess();
      const path = resolvePostLoginPath({
        isStudentOrParticipant,
        roleFlag: data?.roleMenuFlag?.[0],
        parts,
        slug,
        customer_id,
      });
      if (path) {
        navigate(path);
        if (path.includes('/widgets/tournament/')) {
          completeTournamentLoginRedirect();
        }
      }
    } catch (error) {
      handleSignInError(error);
    }
  };

  const signUpUser = async ({
    first_name,
    last_name,
    email,
    user_mobile,
    role_id,
    password,
    parent1_name,
    parent1_mobile,
    gender,
    date_of_birth,
    child,
    campus_id,
  }) => {
    fetchStart();
    try {
      await jwtAxios.post('/auth/register', {
        first_name,
        last_name,
        email,
        user_mobile,
        roles: [role_id],
        password,
        parent1_name,
        parent1_mobile,
        gender,
        date_of_birth,
        child,
        campus_id,
      });
      fetchSuccess();
      navigate('/signin');
      infoViewActionsContext.showMessage('Registered successfully!');
    } catch (error) {
      fetchError(error?.response?.data?.error || 'Something went wrong');
    }
  };

  const logout = async () => {
    try {
      const fcmToken = localStorage.getItem('fcm_token'); // Retrieve stored token

      await jwtAxios.post('/auth/logout', { fcm_token: fcmToken });

      // Clear stored data
      localStorage.removeItem('fcm_token');
      localStorage.removeItem('verbiageSettings');
      localStorage.removeItem('token');
      localStorage.removeItem('set_campus_id');
      localStorage.removeItem('product_status');
      localStorage.removeItem('product_theme_filter');
      localStorage.removeItem('product_display_filter');
      localStorage.removeItem('product_badge');
      localStorage.removeItem('product_date_filter');
      localStorage.removeItem('package_access_method');
      localStorage.removeItem('Package_theme');
      localStorage.removeItem('modalClosed');
      localStorage.removeItem('unsubscribe');
      localStorage.removeItem('theme');
      localStorage.removeItem('current_user');
      clearTournamentRegistrationSession();
      setAuthToken();
      setShowDashboard(true);
      setJWTAuthData({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });

      fetchSuccess();
      navigate('/signin');

      setIsListenerAttached(false);

      const platform = Capacitor.getPlatform();
      if (platform === 'android' || platform === 'ios') {
        await PushNotifications?.removeAllListeners();
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const forgotPassword = async ({ email, customer_id }) => {
    try {
      const response = await jwtAxios.post('/auth/forgot-password', {
        email,
        customer_id,
      });
      fetchSuccess();
      return response;
    } catch (error) {
      fetchError();
      return error;
    }
  };

  const handleCloseErrorDialog = () => {
    setIsErrorDialogOpen(false);
  };
  const externallyAuthenticateUser = async () => {
    fetchStart();
    await jwtAxios
      .get('/auth')
      .then(({ data }) => {
        fetchSuccess();
        setJWTAuthData({
          user: data,
          isLoading: false,
          isAuthenticated: true,
        });
      })
      .catch(() => {
        setJWTAuthData({
          user: undefined,
          isLoading: false,
          isAuthenticated: false,
        });
        fetchSuccess();
      });
  };

  return (
    <>
      <JWTAuthContext.Provider
        value={{//NOSONAR
          ...jWTAuthData,
        }}
      >
        <JWTAuthActionsContext.Provider
          value={{//NOSONAR
            signUpUser,
            signInUser,
            logout,
            forgotPassword,
            externallyAuthenticateUser,
          }}
        >
          {children}
        </JWTAuthActionsContext.Provider>
      </JWTAuthContext.Provider>
      <Dialog
        open={isErrorDialogOpen}
        onClose={() => setIsErrorDialogOpen(false)}
        maxWidth='sm'
        className='locked-accounts'
      >
        <DialogTitle></DialogTitle>
        <DialogContent sx={{ color: '#000' }}>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'crimson',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ErrorOutlineIcon sx={{ marginRight: 2 }} /> Your account has been
            locked. Please reach out to your admin to get it unlocked.
          </Typography>
          <br></br>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseErrorDialog} color='primary'>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

JWTAuthAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  fetchStart: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  fetchSuccess: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
    .isRequired,
  fetchError: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
};

export default JWTAuthAuthProvider;
